import { Payments, TokenResult } from '@square/web-payments-sdk-types';
import SqFetch from 'typescript/application/sq_fetch';
import { createVerificationDetails } from 'typescript/utilities/web_payment_sdk';
import { CardIntent } from 'typescript/constants/enums/card';
import { TOrderSummary, OrderAddress } from 'typescript/types/api/solidshop';
import { trackCdp } from 'utilities/tracking';

// Mirror the legacy CDP event names (app/javascript/utilities/shop_payments.js) so
// historical verifyBuyer dashboards keep working after the TS checkout rewrite.
const VERIFY_BUYER_RESPONSE_EVENT = 'verifyBuyer response';
const VERIFY_BUYER_ERROR_EVENT = 'verifyBuyer error';

// snake_case the SDK payment-method discriminator ('ApplePay' -> 'apple_pay')
// so verifyBuyer dashboards can split card vs wallet outcomes.
const paymentMethodFromTokenResult = (tokenResult: TokenResult): string =>
  (tokenResult.details?.method || 'unknown')
    .replaceAll(/([\da-z])([A-Z])/g, '$1_$2')
    .toLowerCase();

// The Web Payments SDK rejects verifyBuyer with a VerifyBuyerError whose `errors`
// array carries { code, type, message } details — enough to distinguish a decline
// from an abandoned 3DS challenge downstream.
const trackVerifyBuyerError = (
  orderNumber: string,
  saveCardToWallet: boolean,
  cardIntent: CardIntent,
  paymentMethod: string,
  error: unknown,
): void => {
  const sdkError = error as {
    name?: string;
    message?: string;
    errors?: Array<{ code?: string; type?: string }>;
  };
  const detail = sdkError?.errors?.[0];

  trackCdp({
    name: VERIFY_BUYER_ERROR_EVENT,
    params: {
      feature: 'shop',
      surface: 'checkout_v3',
      order_number: orderNumber,
      save_card_to_wallet: saveCardToWallet,
      card_intent: cardIntent,
      payment_method: paymentMethod,
      error_name: sdkError?.name,
      error_message: sdkError?.message,
      error_type: detail?.type,
      error_code: detail?.code,
    },
  });
};

// SqFetch is raw fetch, so a rejection means no response arrived. Tag it here
// so error telemetry can tell network failures apart from browser exceptions.
const postPaymentRequest = (url: string, requestParams: object) =>
  SqFetch.post(url, requestParams).catch((error: unknown) => {
    throw Object.assign(
      error instanceof Error ? error : new Error('Payment request failed'),
      { error_code: 'network_failure' },
    );
  });

interface PaymentDataPayload {
  card_data?: {
    card_brand: string;
    last_4: string;
    exp_year: number;
    exp_month: number;
    // Must match the key the backend reads: credit_card_info[:billing_postal_code]
    // in create_payment_response.rb (persisted as Spree::CreditCard#sq_postal_code).
    billing_postal_code?: string;
  };
  cashtag?: string;
}

const createPaymentDataPayload = (
  tokenResult: TokenResult,
): PaymentDataPayload => {
  if (tokenResult.details?.cashAppPay?.cashtag) {
    return { cashtag: tokenResult.details.cashAppPay.cashtag };
  } else {
    const cardData = tokenResult.details.card!;
    const postalCode = tokenResult.details.billing?.postalCode;
    return {
      card_data: {
        card_brand: cardData.brand,
        last_4: cardData.last4,
        exp_year: cardData.expYear,
        exp_month: cardData.expMonth,
        billing_postal_code: postalCode,
      },
    };
  }
};

// An OK token can still lack what createPaymentDataPayload reads. This checks
// for the token and for card details or a cashtag, not each card field.
const getPaymentDataValidationError = (
  tokenResult: TokenResult,
): string | undefined => {
  if (!tokenResult.token) {
    return 'missing_token';
  }
  if (!tokenResult.details?.cashAppPay?.cashtag && !tokenResult.details?.card) {
    return 'missing_card_details';
  }
  return undefined;
};

const getCardIntent = (
  orderData: TOrderSummary,
  saveCardToWallet: boolean,
): CardIntent => {
  if (saveCardToWallet) {
    return CardIntent.CHARGE_AND_STORE;
  }
  const intent = orderData.card_intent?.intent?.toUpperCase();
  if (intent && Object.values(CardIntent).includes(intent as CardIntent)) {
    return intent as CardIntent;
  }

  return CardIntent.CHARGE;
};

interface CreateShopPaymentWithSavedCardRequestParams {
  order_number: string;
  wallet_payment_source_id: string;
  forter_token: string;
  save_to_wallet: boolean;
  card_data: {
    card_brand: string;
    last_4: string;
    exp_year: number;
    exp_month: number;
    billing_postal_code: string;
  };
}

const createShopPaymentWithSavedCard = (
  orderNumber: string,
  selectedCard: any,
): Promise<Response> => {
  const requestParams: CreateShopPaymentWithSavedCardRequestParams = {
    order_number: orderNumber,
    wallet_payment_source_id: selectedCard.id,
    forter_token: window.ftrToken,
    save_to_wallet: false,
    card_data: {
      card_brand: selectedCard.payment_source.brand,
      last_4: selectedCard.payment_source.last_digits,
      exp_year: selectedCard.payment_source.year,
      exp_month: selectedCard.payment_source.month,
      billing_postal_code:
        selectedCard.payment_source?.address?.postal_code || '',
    },
  };

  return postPaymentRequest('../../api/v1/payments', requestParams);
};

interface CreateShopPaymentWithTokenRequestParams extends PaymentDataPayload {
  order_number: string;
  nonce: string;
  verification_token: string;
  forter_token: string;
  issuer_number_of_installments: number | null;
  save_to_wallet: boolean;
  wallet_payment_source_id: string | null;
}

const createShopPaymentWithToken = async (
  orderNumber: string,
  orderData: TOrderSummary,
  tokenResult: TokenResult,
  sqPayments: Payments,
  issuerInstallmentsTerm: number | null = null,
  saveCardToWallet = false,
  billingAddress?: OrderAddress,
): Promise<Response> => {
  const cardIntent = getCardIntent(orderData, saveCardToWallet);
  const paymentMethod = paymentMethodFromTokenResult(tokenResult);

  let scaResponse: Awaited<ReturnType<typeof sqPayments.verifyBuyer>>;
  try {
    scaResponse = await sqPayments.verifyBuyer(
      tokenResult.token,
      createVerificationDetails(orderData, cardIntent, billingAddress),
    );
  } catch (error) {
    trackVerifyBuyerError(
      orderNumber,
      saveCardToWallet,
      cardIntent,
      paymentMethod,
      error,
    );
    throw error;
  }

  trackCdp({
    name: VERIFY_BUYER_RESPONSE_EVENT,
    params: {
      feature: 'shop',
      surface: 'checkout_v3',
      order_number: orderNumber,
      user_challenged: scaResponse?.userChallenged ?? false,
      save_card_to_wallet: saveCardToWallet,
      card_intent: cardIntent,
      payment_method: paymentMethod,
      // A resolved-but-tokenless response yields an unusable verification_token.
      has_verification_token: Boolean(scaResponse?.token),
    },
  });

  if (!scaResponse) {
    // verifyBuyer's return type includes null; tracked above, fail the submit here.
    throw new TypeError('verifyBuyer resolved without a response');
  }

  const requestParams: CreateShopPaymentWithTokenRequestParams = {
    order_number: orderNumber,
    nonce: tokenResult.token,
    verification_token: scaResponse.token, // buyer verification token
    forter_token: window.ftrToken,
    issuer_number_of_installments: issuerInstallmentsTerm,
    save_to_wallet: saveCardToWallet,
    wallet_payment_source_id: null,
    ...createPaymentDataPayload(tokenResult),
  };

  return postPaymentRequest('../../api/v1/payments', requestParams);
};

interface PreAuthRequestParams extends PaymentDataPayload {
  order_number: string;
  nonce: string;
  forter_token: string;
}

const checkPreAuthFraud = (
  orderNumber: string,
  tokenResult: TokenResult,
): Promise<Response | null> => {
  try {
    const requestParams: PreAuthRequestParams = {
      order_number: orderNumber,
      nonce: tokenResult.token,
      forter_token: window.ftrToken,
      ...createPaymentDataPayload(tokenResult),
    };
    return postPaymentRequest('../../api/v1/pre-auth', requestParams);
  } catch {
    return null;
  }
};

export {
  createShopPaymentWithToken,
  createShopPaymentWithSavedCard,
  checkPreAuthFraud,
  getPaymentDataValidationError,
};
